import {
  put,
  call,
  takeLatest,
  select,
  delay,
  retry,
} from 'redux-saga/effects';
import { replace } from 'connected-react-router';
import _isEmpty from 'lodash/isEmpty';
import _includes from 'lodash/includes';
import _find from 'lodash/find';
import _isArray from 'lodash/isArray';
import ActionTypes from '../constants/ActionTypes';
import { progressStart, progressStop } from '../actions';
import {
  getSectionWiseProblems,
  submitTestApi,
  submitSectionApi,
  sumbitFeedbackApi,
  reportIssueApi,
} from '../api/test';
import { showErrorToast, showSuccessToast } from '../actions/toast';
import {
  getSolutionSet,
  getTest,
  submitTest,
  updateTestExpiryAndSessionState,
} from '../actions/test';
import {
  disableSnapshotInterval,
  disableWebcam,
  updateSignedUrlsFromCache,
} from '../actions/proctor';
import { trackFinishTest, trackSubmitSection } from '../actions/eventTracking';
import {
  getApiErrorData,
  removingProctoringSessionData,
  removingWebcamErrorData,
} from '../utils/helperFunction';
import {
  errorLogger,
  proctoringUBATracker,
  proctoringUBALogger,
} from '../utils/trackingUtil';
import { proctorHeartBeatLogger } from '../api/proctor';
import { codingProblemTypes } from '../constants/ProblemTypes';
import {
  FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY,
  PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY,
  proctorRetryLoaderMessage,
  IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY,
} from '../constants/Proctor';
import {
  AUTO_SUBMIT_REASONS,
  SYSTEM_TIME_MISMATCH_TOAST_MESSAGE,
  TEST_SUBMIT_RETRY_COUNTER,
  TEST_SUBMIT_RETRY_DELAY_IN_MILLISECONDS,
  SECTION_SUBMIT_API_VERSION,
  EVALUATION_STATE,
} from '../constants/Constants';
import { SESSION_STATE } from '../constants/AssessmentConstants';

import {
  getAndUpdateWebsocketSequence,
  getCurrentTime,
  getEndTimeLocalStorageKey,
  getWebsocketSequenceStorageKey,
  getServerTime,
  deleteIndexedDB,
  getFailedSocketEventsByNames,
} from '../utils/utils';
import { getServerTimeAndTrackingIds } from './auth';
import { redirectAfterTestSubmit } from './utils';
import { updatePodBasedTestState } from '../actions/podBased';
import {
  EAudioRecordingStatus,
  AUDIO_DB,
} from '../components/ProblemTypes/Audio/constants';
import { VIDEO_DB } from '../components/ProblemTypes/Video/constants';
import { SOCKET_EVENTS } from '../constants/Socket';

function* updateMessageWithSeconds({ seconds }) {
  if (seconds) {
    const message = proctorRetryLoaderMessage(seconds);
    yield put(progressStart(message));
    yield delay(1000);
    yield call(updateMessageWithSeconds, { seconds: seconds - 1 });
  }
}

function* finishTest({ isForceSubmit }) {
  try {
    yield put({
      type: ActionTypes.SET_TEST_SUBMIT_IN_PROCESS,
      payload: true,
    });
    yield put({
      type: ActionTypes.SET_TEST_SUBMITTING,
      payload: true,
    });
    const test = yield select(state => state.test);
    const isVideoUploading = Object.values(test.isUploadInProgress).filter(
      inProgress => inProgress === true
    );
    const isAudioUploading = Object.values(test.isAudioUploadInProgress).filter(
      inProgress => inProgress === EAudioRecordingStatus.UPLOADING
    );
    if (isVideoUploading.length === 0 && isAudioUploading.length === 0)
      yield put(progressStart());
    const assessmentId = yield select(state => state.assessment.assessmentId);
    const testSubmitTime = new Date();
    const testSolutionSet = yield select(state => state.testSolutionSet);
    const inSolutionPage = _includes(window.location.pathname, '/problem/');
    const submissionType = isForceSubmit === true ? 'ASB' : 'MSB';
    yield call(getServerTimeAndTrackingIds, { isLastCall: true });
    /**
     * Since coding problems save once every 60 seconds. If the current problem
     * that the user is attempting is a coding problem, we save the problem
     * before submitting the test
     */
    if (inSolutionPage) {
      const problemSlug = window.location.pathname.split('/')[4];
      let isCodingProblem = false;
      const sectionsNotGrouped = yield select(
        state => state.sections.sectionsNotGrouped
      );
      const solution = yield select(state => state.coding.solution);
      let problem;
      _find(sectionsNotGrouped, section => {
        let temp;
        if (_isArray(section.problems))
          temp = _find(section.problems, item => {
            if (
              item.slug === problemSlug &&
              codingProblemTypes.includes(item.problem_type)
            ) {
              problem = item;
              return true;
            }
            return false;
          });
        if (temp) {
          isCodingProblem = true;
          return true;
        }
        return false;
      });
      if (problem && isCodingProblem && solution[problemSlug].slug) {
        yield put({
          type: ActionTypes.SAVE_CODE_NO_DEBOUNCE,
          saveCode: true,
          problem,
          code: undefined,
        });
      }
    }
    if (_isEmpty(testSolutionSet)) {
      return;
    }
    if (isForceSubmit) {
      yield put({
        type: ActionTypes.DISCONNECT_SOCKET,
      });
    }

    yield put({
      type: ActionTypes.SET_PROCTOR_IS_TEST_SUBMITTED,
      payload: true,
    });

    let imageSequenceData = sessionStorage.getItem(
      PROCTOR_IMAGE_SEQUENCE_DETAILS_LOCALSTORAGE_KEY
    );
    imageSequenceData = imageSequenceData ? JSON.parse(imageSequenceData) : {};
    const proctorSessionId = yield select(
      state => state.proctor?.proctorSessionId
    );

    proctoringUBATracker({
      additionalInfo: `Triggered before final heartbeat cadidateCpuCore:${navigator?.hardwareConcurrency}`,
    });

    const {
      loggerPostCall: logProctorHeartBeat,
      payload,
    } = proctorHeartBeatLogger({
      token: testSolutionSet?.token,
      assessmentId: testSolutionSet?.id,
      imageData: imageSequenceData,
      final: true,
      proctorSessionId,
    });
    logProctorHeartBeat.catch(e => errorLogger(e, payload, 'final heartbeat'));
    let { testEndTime } = test;
    if (assessmentId && !testEndTime) {
      testEndTime = localStorage.getItem(
        getEndTimeLocalStorageKey(assessmentId)
      );
    }
    // for manual submit there is no delay on submit, so we are sending time from here,
    // here we are storing time because, if the api call send to backend
    // but front-end didn't received response due to network error,
    // in this case we again need to send the same time
    if (!testEndTime && !isForceSubmit) {
      testEndTime = getCurrentTime().toISOString();
      if (assessmentId)
        localStorage.setItem(
          getEndTimeLocalStorageKey(assessmentId),
          testEndTime
        );
    }
    proctoringUBATracker({
      additionalInfo: `Triggered after final heartbeat cadidateCpuCore:${navigator?.hardwareConcurrency}`,
    });

    const { isSystemTimeMismatch, autoSubmitReason } = yield select(
      state => state.appStatus
    );

    if (isSystemTimeMismatch) {
      yield put(showErrorToast(SYSTEM_TIME_MISMATCH_TOAST_MESSAGE));
    }

    let submitReason =
      isSystemTimeMismatch && !autoSubmitReason
        ? AUTO_SUBMIT_REASONS.SYSTEM_TIME_MISMATCH
        : autoSubmitReason;

    // Format WEBCAM_ERROR reason with timeout value
    if (submitReason === AUTO_SUBMIT_REASONS.WEBCAM_ERROR) {
      const timeoutSeconds =
        test?.settings?.proctor?.strict_camera?.inactive_timeout;
      if (timeoutSeconds > 0) {
        submitReason = `Camera not detected for ${timeoutSeconds} seconds`;
      }
    }

    proctoringUBALogger(
      'info:autosubmit-system-time-mismatch-on-submit',
      JSON.stringify({
        isSystemTimeMismatch,
        autoSubmitReason,
        submitReason,
      })
    );

    const websocketEventSequence = getAndUpdateWebsocketSequence(
      testSolutionSet.id
    );

    // collect failed socket events from sessionStorage and filter only BLUR and FOCUS
    const failedSocketEvents = getFailedSocketEventsByNames([
      SOCKET_EVENTS.BLUR_EVENT,
      SOCKET_EVENTS.FOCUS_EVENT,
    ]);

    yield retry(
      TEST_SUBMIT_RETRY_COUNTER,
      TEST_SUBMIT_RETRY_DELAY_IN_MILLISECONDS,
      submitTestApi,
      {
        testSolutionSetId: testSolutionSet.id,
        submissionType,
        proctoringSubmitTime: testSubmitTime.toISOString(),
        testSolutionSetEndTime: testEndTime,
        reason: submissionType === 'ASB' ? submitReason : '',
        websocketEventSequence,
        failedSocketEvents,
      }
    );
    const failedProctorImages = localStorage.getItem(
      FAILED_IMAGE_PROCTOR_UPLOAD_LOCALSTORAGE_KEY
    );
    const failedProctorUploadImages = failedProctorImages
      ? JSON.parse(failedProctorImages)
      : {};
    const failedProctorUploadImageCount = Object.keys(failedProctorUploadImages)
      ?.length;
    if (failedProctorUploadImages && failedProctorUploadImageCount > 0) {
      yield call(updateMessageWithSeconds, {
        seconds: failedProctorUploadImageCount,
      });
    }
    /**
     * calling the API to get the current test status takes a second
     * before fetch the data, so the webcam that we have disabled below
     * will get enabled again. So we set the test as expired here manually
     */
    test.is_expired = true;
    yield put({
      type: `${ActionTypes.GET_TEST}_SUCCESS`,
      payload: test,
    });
    // pod based project checks
    const { testProjectStates } = yield select(state => state.podBased);
    const testState = testProjectStates;
    Object.keys(testProjectStates).forEach(problemTestState => {
      const evalSocket = testProjectStates[problemTestState]?.socketId;
      if (evalSocket) {
        evalSocket.close();
        testState[problemTestState].socketId = undefined;
        testState[problemTestState].evaluationState = EVALUATION_STATE.ABORT;
      }
    });
    yield put(updatePodBasedTestState({ ...testState }));
    yield put(disableSnapshotInterval());
    yield put(disableWebcam());
    removingProctoringSessionData();
    removingWebcamErrorData();
    deleteIndexedDB(AUDIO_DB);
    deleteIndexedDB(VIDEO_DB);
    yield put(updateSignedUrlsFromCache({}));
    localStorage.removeItem(IMAGE_SEQUENCE_IN_RESUMED_TEST_LOCALSTORAGE_KEY);
    const websocketEventSequenceKey = getWebsocketSequenceStorageKey(
      assessmentId
    );
    sessionStorage.removeItem(websocketEventSequenceKey);
    localStorage.removeItem(`DoSelect.tc${test.slug}`);
    yield put(trackFinishTest());
    // eventually we call the API to get the test status
    yield put(getTest(test.slug));
    yield call(redirectAfterTestSubmit);
  } catch (e) {
    errorLogger(e);
    const { toast } = yield select(state => ({
      toast: state.appStatus.toast,
      slug: state.test.slug,
    }));
    if (!isForceSubmit) {
      yield put({
        type: `${ActionTypes.SUBMIT_TEST}_FAIL`,
      });
      const errorInfo = getApiErrorData(e, 'SMT');
      if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
        yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
      }
    }
    if (e.response?.data?.action?.redirection || isForceSubmit) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield call(redirectAfterTestSubmit);
    }
  } finally {
    yield put(progressStop());
    yield put({
      type: ActionTypes.SET_TEST_SUBMIT_IN_PROCESS,
      payload: false,
    });
  }
}

function* submitSection(args) {
  try {
    yield put(progressStart());
    const { sections, test } = yield select(state => ({
      sections: state.testSolutionSet.sections,
      test: state.test,
    }));
    const sectionDetails = sections?.find(
      section => section.slug === args.sectionSlug
    );
    yield call(submitSectionApi, {
      solutionset_id: args.solutionsetId,
      section_slug: args.sectionSlug,
      force_submission: args.forceSubmission,
      created_at: getServerTime().toISOString(),
      version: SECTION_SUBMIT_API_VERSION,
      section_name: sectionDetails?.name,
    });
    yield put(getSolutionSet(test.id, true));
    const testSolutionSet = yield select(state => state.testSolutionSet);
    const resp = yield call(
      getSectionWiseProblems,
      test.id,
      test.slug,
      test?.sub_test_type
    );
    const isTimedSection = test.is_timed_section === true;
    let forSolutionPage = [];
    // if the test does not have sections that are timed
    // then move all problems into 1st section
    if (!isTimedSection) {
      forSolutionPage = [resp.data[0]];
      resp.data.forEach((section, index) => {
        if (index)
          forSolutionPage[0].problems = forSolutionPage[0].problems.concat(
            section.problems
          );
      });
    } else {
      forSolutionPage = resp.data;
      /**
       * Check if it is time based sections, check if extra time is added,
       * and check if there is any section that is 'currently taking'
       */
      if (testSolutionSet.add_test_duration > 0) {
        let hasActiveSection = false;
        forSolutionPage.find(section => {
          if (section.status === 'CTK') hasActiveSection = true;
          return hasActiveSection;
        });
        if (hasActiveSection === false) {
          yield put(submitTest());
          return;
        }
      }
    }

    yield put({
      type: `${ActionTypes.GET_SECTIONS}_SUCCESS`,
      payload: {
        forSolutionPage,
        sectionsNotGrouped: resp.data,
      },
    });

    // ADP UX: when section submission is triggered from last-question flow,
    // redirect to All Problems page instead of auto-opening next section question.
    if (args?.redirectToProblems) {
      yield put(replace(`/test/${test.slug}/problems`));
      yield put(trackSubmitSection());
      return;
    }

    const currentSectionIndex = sections.findIndex(
      section => section.slug === args.sectionSlug
    );
    if (
      !_isEmpty(sections[currentSectionIndex]) &&
      !_isEmpty(sections[currentSectionIndex + 1]) &&
      !_isEmpty(sections[currentSectionIndex + 1].problems)
    ) {
      const [nextProbSlug] = sections[currentSectionIndex + 1].problems;
      if (!_isEmpty(nextProbSlug)) {
        yield put(replace(`/test/${test.slug}/problem/${nextProbSlug}`));
      }
    }
    yield put(trackSubmitSection());
  } catch (e) {
    const { toast, isTestSubmitted } = yield select(state => ({
      toast: state.appStatus.toast,
      isTestSubmitted: state.test.isTestSubmitted,
      slug: state.test.slug,
    }));
    let errorInfo = {};
    if (e?.config?.url?.includes('test.submit_section')) {
      errorInfo = getApiErrorData(e, 'SMS');
    } else if (
      e?.config?.url?.includes('sectionwise/problems') &&
      !isTestSubmitted
    ) {
      errorInfo = getApiErrorData(e, 'GSP');
    }
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (e.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield call(redirectAfterTestSubmit);
    }
  } finally {
    yield put(progressStop());
  }
}
function* sumbitFeedback(args) {
  try {
    yield put(progressStart());
    const test = yield select(state => state.test);
    yield call(sumbitFeedbackApi, args.payload);
    yield put({
      type: `${ActionTypes.GET_FEEDBACK}_SUCCESS`,
      payload: { feedback: true },
    });
    if (test.redirection_url) {
      window.location.href = test.redirection_url;
    } else if (test.test_type === 'LRN') {
      /**
       * If there is no redirection url available and if the
       * test is part of learn then redirect to report page
       */
      yield put(replace(`/test-report/${test.slug}/`));
    }
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'SFB');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}

function* reportIssue(args) {
  try {
    yield put(progressStart());
    yield call(reportIssueApi, args.payload);
    yield put(showSuccessToast('Your issue has been reported succesfully'));
  } catch (e) {
    const { toast } = yield select(state => state.appStatus);
    const errorInfo = getApiErrorData(e, 'RAS');
    if (errorInfo && errorInfo.message && !toast?.apiErrorInfo) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
  } finally {
    yield put(progressStop());
  }
}

export default function* loadSubmitSaga() {
  yield takeLatest(ActionTypes.SUBMIT_TEST, finishTest);
  yield takeLatest(ActionTypes.SUBMIT_SECTION, submitSection);
  yield takeLatest(ActionTypes.SUBMIT_FEEDBACK, sumbitFeedback);
  yield takeLatest(ActionTypes.REPORT_ISSUE, reportIssue);
}
